import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { post } from '@/lib/api';
import { trackError } from '@/lib/error-tracking';
import { app_queries_path } from '@/rails/routes';
import { isAxiosError } from 'axios';
import { useState, type FormEvent } from 'react';

interface SqlTabProps {
  appId: string;
  access: 'read' | 'write';
  databaseMode: 'shared' | 'dedicated' | null;
  canManage: boolean;
}

interface QueryResult {
  rows: Record<string, unknown>[];
  truncated: boolean;
  demo: boolean;
  // A write that broke the access rules takes a published app down.
  unpublished: boolean;
  problems: string[];
}

const UNEXPECTED = 'The query could not be run. Try again.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// The response body is untyped. Anything that is not a result is treated as a failure.
function readResult(body: unknown): QueryResult | null {
  if (isRecord(body) && Array.isArray(body.rows) && body.rows.every(isRecord)) {
    const problems = Array.isArray(body.problems)
      ? body.problems.filter((problem): problem is string => typeof problem === 'string')
      : [];

    return {
      rows: body.rows,
      truncated: body.truncated === true,
      demo: body.demo === true,
      unpublished: body.unpublished === true,
      problems,
    };
  }

  return null;
}

// The server explains a refused query as { error }. Null means it did not.
function serverMessage(error: unknown): string | null {
  if (isAxiosError(error)) {
    const body: unknown = error.response?.data;

    if (isRecord(body) && typeof body.error === 'string') return body.error;
  }

  return null;
}

function columnsOf(rows: Record<string, unknown>[]): string[] {
  const columns = new Set<string>();
  rows.forEach((row) => Object.keys(row).forEach((column) => columns.add(column)));

  return [...columns];
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
}

const SqlTab = ({ appId, access, databaseMode, canManage }: SqlTabProps) => {
  const [sql, setSql] = useState('');
  const [writes, setWrites] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dedicated = databaseMode === 'dedicated';
  // A write also needs an owner or admin; the server refuses anyone else.
  const canWrite = access === 'write' && canManage;
  const columns = result ? columnsOf(result.rows) : [];

  const run = async (write: boolean) => {
    setRunning(true);
    setError(null);

    try {
      const response = await post(app_queries_path(appId), { sql, write, confirmed: write });
      const next = readResult(response.data);

      if (next) {
        setResult(next);
      } else {
        setResult(null);
        setError(UNEXPECTED);
        trackError('Developer SQL returned a response that is not a result');
      }
    } catch (caught) {
      const message = serverMessage(caught);

      setResult(null);
      setError(message ?? UNEXPECTED);
      if (message === null) trackError('Developer SQL query failed', caught);
    } finally {
      setRunning(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (canWrite && writes) {
      setConfirming(true);
    } else {
      void run(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Run SQL</h2>
          </CardTitle>
          <CardDescription>
            {dedicated
              ? "Runs against this app's own database. Statements are read-only unless you say otherwise."
              : 'SQL runs on a dedicated database (Build and Studio).'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <Label htmlFor="developer-sql">SQL</Label>
            <Textarea
              id="developer-sql"
              value={sql}
              onChange={(event) => setSql(event.target.value)}
              rows={6}
              spellCheck={false}
              className="font-mono text-sm"
              placeholder="select * from public.jobs limit 20;"
              disabled={!dedicated}
              required
            />

            {canWrite ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="developer-sql-writes"
                  checked={writes}
                  onCheckedChange={(checked) => setWrites(checked === true)}
                  disabled={!dedicated}
                />
                <Label htmlFor="developer-sql-writes">This statement writes</Label>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                Read-only. Writing with SQL is part of Studio.
              </p>
            )}

            <Button type="submit" disabled={!dedicated || running}>
              {running ? 'Running…' : 'Run'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div aria-live="polite" className="space-y-4">
        {error ? (
          <p role="alert" className="text-sm break-words text-red-700">
            {error}
          </p>
        ) : null}

        {result?.unpublished ? (
          <div
            role="alert"
            className="border-destructive text-destructive border-l-2 pl-3 text-sm leading-relaxed"
          >
            <p className="font-medium">
              The app is unpublished. After this write, its access rules no longer pass.
            </p>
            <ul className="mt-1 list-disc pl-5">
              {result.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
            <p className="mt-1">Fix the rules, then publish again from the Publish page.</p>
          </div>
        ) : null}

        {result ? (
          <Card>
            <CardHeader>
              <CardTitle>
                <h2>Result</h2>
              </CardTitle>
              <CardDescription>
                {result.rows.length === 1 ? '1 row' : `${result.rows.length} rows`}
                {result.truncated ? '. More rows matched and were left out.' : '.'}
                {result.demo
                  ? ' Demo database: no rows come back until Supabase is connected.'
                  : ''}
              </CardDescription>
            </CardHeader>
            {result.rows.length > 0 ? (
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((column) => (
                        <TableHead key={column}>
                          <code className="text-xs">{column}</code>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((row, index) => (
                      <TableRow key={index}>
                        {columns.map((column) => (
                          <TableCell key={column} className="font-mono text-xs">
                            {row[column] === null || row[column] === undefined ? (
                              <span className="text-muted-foreground">NULL</span>
                            ) : (
                              cellText(row[column])
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            ) : null}
          </Card>
        ) : null}
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run a statement that writes?</AlertDialogTitle>
            <AlertDialogDescription>
              This changes the app&apos;s database directly. It is not checked against the design
              and it cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run(true)}>Run the write</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SqlTab;
